import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { FormField } from './FormField';

/**
 * Convenience wrappers over `FormField` for the two most common controls.
 *
 * These exist because ~90% of the ported fields are a plain input or textarea inside a
 * FormField, and repeating the render prop at every call site would add noise without
 * adding clarity. Reach for `FormField` directly when the control is a select,
 * combobox, or anything with its own trigger element.
 *
 * Designed to accept a spread `register()` result:
 * ```tsx
 * <TextField label="Trade License No" required
 *            error={errors.tradeLicenseNo?.message}
 *            hasValue={!!watch('tradeLicenseNo')}
 *            {...register('tradeLicenseNo')} />
 * ```
 */

type SharedFieldProps = {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  hasValue?: boolean;
  alwaysRaised?: boolean;
  fieldClassName?: string;
  dense?: boolean;
};

/**
 * `prefix` and `suffix` must be omitted from the native attributes: `HTMLAttributes` declares
 * `prefix?: string` (the RDFa attribute), so intersecting it with our `ReactNode` props
 * narrows them to `string` and rejects an <Icon>.
 */
export type TextFieldProps = SharedFieldProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id' | 'prefix' | 'suffix'>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    required,
    hint,
    error,
    prefix,
    suffix,
    hasValue,
    alwaysRaised,
    fieldClassName,
    dense,
    disabled,
    onFocus,
    onBlur,
    value,
    defaultValue,
    type = 'text',
    ...rest
  },
  ref,
) {
  // A date input can never show its label inside the notch (the browser renders
  // dd/mm/yyyy immediately), so it must stay raised.
  const raised = alwaysRaised ?? (type === 'date' || type === 'time' || type === 'datetime-local');
  const filled =
    hasValue ?? (value !== undefined ? value !== '' && value !== null : undefined);

  return (
    <FormField
      label={label}
      required={required}
      hint={hint}
      error={error}
      prefix={prefix}
      suffix={suffix}
      disabled={disabled}
      hasValue={filled}
      alwaysRaised={raised}
      className={fieldClassName}
      dense={dense}
    >
      {(field) => (
        <input
          {...rest}
          ref={ref}
          type={type}
          id={field.id}
          value={value}
          defaultValue={defaultValue}
          disabled={field.disabled}
          className={field.className}
          aria-invalid={field['aria-invalid']}
          aria-describedby={field['aria-describedby']}
          aria-required={field['aria-required']}
          onFocus={(event) => {
            field.onFocus();
            onFocus?.(event);
          }}
          onBlur={(event) => {
            field.onBlur();
            onBlur?.(event);
          }}
        />
      )}
    </FormField>
  );
});

export type TextAreaFieldProps = SharedFieldProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'id' | 'prefix' | 'suffix'>;

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField(
    {
      label,
      required,
      hint,
      error,
      prefix,
      suffix,
      hasValue,
      alwaysRaised,
      fieldClassName,
      dense,
      disabled,
      onFocus,
      onBlur,
      value,
      defaultValue,
      rows = 3,
      ...rest
    },
    ref,
  ) {
    const filled =
      hasValue ?? (value !== undefined ? value !== '' && value !== null : undefined);

    return (
      <FormField
        label={label}
        required={required}
        hint={hint}
        error={error}
        prefix={prefix}
        suffix={suffix}
        disabled={disabled}
        hasValue={filled}
        alwaysRaised={alwaysRaised}
        className={fieldClassName}
        dense={dense}
      >
        {(field) => (
          <textarea
            {...rest}
            ref={ref}
            id={field.id}
            rows={rows}
            value={value}
            defaultValue={defaultValue}
            disabled={field.disabled}
            className={field.className}
            aria-invalid={field['aria-invalid']}
            aria-describedby={field['aria-describedby']}
            aria-required={field['aria-required']}
            onFocus={(event) => {
              field.onFocus();
              onFocus?.(event);
            }}
            onBlur={(event) => {
              field.onBlur();
              onBlur?.(event);
            }}
          />
        )}
      </FormField>
    );
  },
);
